import {
  fetchLeaderboard,
  fetchRankedLeaderboard,
  type LeaderEntry,
  type RankedLeaderEntry,
} from '../auth/api.js';
import { rankFromPoints } from '../game/rank.js';
import { el } from './login.js';

/**
 * Two-tab leaderboard screen:
 *   • Classique — sorted by games_won DESC (legacy).
 *   • Classé    — sorted by ranked_points DESC, shows the rank tier.
 *
 * Tab choice is persisted in the URL hash via `?tab=classed` so a
 * direct link / refresh keeps the selected tab.
 */
type Tab = 'classic' | 'ranked';

export async function renderLeaderboard(container: HTMLElement): Promise<void> {
  container.innerHTML = '';
  container.classList.add('overlay-screen');

  const wrap = el('div', 'leader-wrap');
  const leaderTitle = document.createElement('h1');
  leaderTitle.className = 'leader-title';
  const leaderIcon = document.createElement('img');
  leaderIcon.src = '/sprites/iconchat_valide.png';
  leaderIcon.className = 'page-title-icon';
  leaderIcon.alt = '';
  leaderTitle.appendChild(leaderIcon);
  leaderTitle.appendChild(document.createTextNode('Tableau des moustaches'));
  wrap.appendChild(leaderTitle);

  // Tab strip
  // History-API mode : read the query string from location.search
  // (e.g. `/leaderboard?tab=ranked`). Falls back to the legacy hash
  // form (`#?tab=ranked`) so old bookmarks still pick the right tab.
  const initialTab: Tab =
    (window.location.search.includes('tab=ranked') ||
     window.location.hash.includes('tab=ranked'))
      ? 'ranked' : 'classic';
  let activeTab: Tab = initialTab;
  const tabs = el('div', 'leader-tabs');
  const tabClassic = makeTab('Patrouilles libres', 'classic');
  const tabRanked  = makeTab('Patrouilles classées', 'ranked');
  tabs.append(tabClassic, tabRanked);
  wrap.appendChild(tabs);

  const sub = el('p', 'leader-sub', 'Chargement…');
  wrap.appendChild(sub);
  const tableHost = el('div', 'leader-table-host');
  wrap.appendChild(tableHost);
  container.appendChild(wrap);

  void renderActive();

  function makeTab(label: string, tab: Tab): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'leader-tab' + (activeTab === tab ? ' is-active' : '');
    b.textContent = label;
    b.addEventListener('click', () => {
      if (activeTab === tab) return;
      activeTab = tab;
      tabClassic.classList.toggle('is-active', activeTab === 'classic');
      tabRanked.classList.toggle('is-active', activeTab === 'ranked');
      void renderActive();
    });
    return b;
  }

  async function renderActive(): Promise<void> {
    sub.textContent = 'Chargement…';
    tableHost.innerHTML = '';
    if (activeTab === 'classic') {
      await renderClassic(sub, tableHost);
    } else {
      await renderRanked(sub, tableHost);
    }
  }
}

// ─── CLASSIC ────────────────────────────────────────────────────────────────

async function renderClassic(sub: HTMLElement, host: HTMLElement): Promise<void> {
  let data;
  try { data = await fetchLeaderboard(); }
  catch { sub.textContent = 'Impossible de charger le classement.'; return; }
  if (data.top.length === 0) {
    sub.textContent = "Aucun chat n'a encore fini sa première patrouille. Sois le premier à griffer !";
    return;
  }
  sub.textContent = `${data.totalRanked} chaton${data.totalRanked > 1 ? 's' : ''} ${data.totalRanked > 1 ? 'pesés' : 'pesé'} sur la balance`;
  const table = makeTable(['Place', 'Royaume', 'Patrouilles', 'Gagnées', 'Win %']);
  const tbody = table.querySelector('tbody')!;
  for (const entry of data.top) tbody.appendChild(classicRow(entry));
  if (data.me) {
    tbody.appendChild(separator(5));
    tbody.appendChild(classicRow(data.me));
  }
  host.appendChild(table);
}

function classicRow(entry: LeaderEntry): HTMLTableRowElement {
  const tr = document.createElement('tr');
  if (entry.isMe) tr.className = 'leader-row-me';
  tr.append(
    cell(`#${entry.rank}`, 'leader-rank'),
    cell(entry.username, 'leader-name'),
    cell(entry.gamesPlayed.toString()),
    cell(entry.gamesWon.toString()),
    cell(entry.gamesPlayed > 0 ? `${Math.round((entry.gamesWon / entry.gamesPlayed) * 100)}%` : '—'),
  );
  return tr;
}

// ─── RANKED ─────────────────────────────────────────────────────────────────

async function renderRanked(sub: HTMLElement, host: HTMLElement): Promise<void> {
  let data;
  try { data = await fetchRankedLeaderboard(); }
  catch { sub.textContent = 'Impossible de charger le classement classé.'; return; }
  if (data.top.length === 0) {
    sub.textContent = "Aucune patrouille classée n'a encore eu lieu. Lance la chasse !";
    return;
  }
  sub.textContent = `${data.totalRanked} chaton${data.totalRanked > 1 ? 's' : ''} en chasse classée`;
  const table = makeTable(['Place', 'Rang', 'Royaume', 'Points', 'Patrouilles', 'Win']);
  const tbody = table.querySelector('tbody')!;
  for (const entry of data.top) tbody.appendChild(rankedRow(entry));
  if (data.me) {
    tbody.appendChild(separator(6));
    tbody.appendChild(rankedRow(data.me));
  }
  host.appendChild(table);
}

function rankedRow(entry: RankedLeaderEntry): HTMLTableRowElement {
  const tr = document.createElement('tr');
  if (entry.isMe) tr.className = 'leader-row-me';
  const resolved = rankFromPoints(entry.rankedPoints);
  // Tier cell — metallic-framed iconchat + tier name. Same `.rank-frame`
  // recipe as the profile's rank card, just smaller.
  const tierTd = document.createElement('td');
  tierTd.className = 'leader-tier';
  const frame = document.createElement('div');
  frame.className = 'leader-tier-frame rank-frame';
  frame.style.setProperty('--rank-tint', resolved.tier.tintColor);
  frame.style.setProperty('--rank-tint-deep', resolved.tier.tintColorDeep);
  const tierIcon = document.createElement('img');
  tierIcon.src = resolved.tier.icon;
  tierIcon.className = 'leader-tier-icon';
  tierIcon.alt = '';
  frame.appendChild(tierIcon);
  tierTd.appendChild(frame);
  const tierName = document.createElement('span');
  tierName.className = 'leader-tier-name';
  tierName.textContent = resolved.tier.name;
  tierTd.appendChild(tierName);

  tr.append(
    cell(`#${entry.rank}`, 'leader-rank'),
    tierTd,
    cell(entry.username, 'leader-name'),
    cell(entry.rankedPoints.toString()),
    cell(entry.rankedGamesPlayed.toString()),
    cell(entry.rankedGamesWon.toString()),
  );
  return tr;
}

// ─── helpers ────────────────────────────────────────────────────────────────

function makeTable(headers: string[]): HTMLTableElement {
  const t = document.createElement('table');
  t.className = 'leader-table';
  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const h of headers) {
    const th = document.createElement('th');
    th.textContent = h;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  t.appendChild(thead);
  t.appendChild(document.createElement('tbody'));
  return t;
}

function cell(text: string, className?: string): HTMLTableCellElement {
  const td = document.createElement('td');
  if (className) td.className = className;
  td.textContent = text;
  return td;
}

function separator(colSpan: number): HTMLTableRowElement {
  const sep = document.createElement('tr');
  sep.className = 'leader-sep';
  const td = document.createElement('td');
  td.colSpan = colSpan;
  td.textContent = '· · ·';
  sep.appendChild(td);
  return sep;
}
